import { useState } from 'react';
import { api } from '../lib/api';

type Plan = { name: string; description: string; episodes: { title: string; angle: string }[] };

// The director plans a series: name a subject, get a run of four to six
// episodes in the order a viewer would watch them. One yes makes the series
// and its cards, each with the angle as its brief and its part number set.
export function PlanSeries({ brandId, onMade }: { brandId: string | null; onMade: (firstCardId: string) => void | Promise<void> }) {
  const [subject, setSubject] = useState('');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [busy, setBusy] = useState<'plan' | 'make' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState(false);
  const [open, setOpen] = useState(false);
  if (!brandId) return null;
  const ask = (e: React.FormEvent) => {
    e.preventDefault(); if (subject.trim().length < 3) return;
    setBusy('plan'); setError(null); setMade(false);
    api.planSeries(brandId, subject.trim()).then(setPlan).catch((er: Error) => setError(er.message)).finally(() => setBusy(null));
  };
  const make = async () => {
    if (!plan) return; setBusy('make'); setError(null);
    try {
      const s = await api.createSeries({ brandId, name: plan.name, description: plan.description || null });
      let first: string | null = null;
      for (const [i, ep] of plan.episodes.entries()) {
        const card = await api.createCard({ title: ep.title, brandId, topic: subject.trim().slice(0, 120) });
        await api.saveBrief(card.id, ep.angle).catch(() => undefined);
        await api.setCardSeries(card.id, s.id, i + 1).catch(() => undefined);
        first ??= card.id;
      }
      setMade(true);
      if (first) await onMade(first);
    } catch (er) { setError((er as Error).message); } finally { setBusy(null); }
  };
  return (
    <div data-tool="plan-series" style={{ margin: '4px 0 8px' }}>
      {!open ? <button type="button" className="btn tiny" onClick={() => setOpen(true)} title="Name a subject; the director proposes a run of episodes in order">Plan a series</button> : (
        <form className="row wrap" style={{ gap: 6, alignItems: 'center' }} onSubmit={ask}>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder="A series about…" style={{ minWidth: 240 }} autoFocus aria-label="Series subject" />
          <button type="submit" className="btn tiny pri" disabled={busy !== null || subject.trim().length < 3}>{busy === 'plan' ? 'Planning…' : 'Plan it'}</button>
          <button type="button" className="btn tiny" onClick={() => { setOpen(false); setPlan(null); }}>Close</button>
        </form>
      )}
      {error && <div className="err inline">{error}</div>}
      {plan && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 10, marginTop: 6 }} data-tool="series-plan">
          <div style={{ fontWeight: 600 }}>{plan.name} <span className="chip">{plan.episodes.length} episodes</span></div>
          {plan.description && <p className="sub-dim" style={{ margin: '2px 0 6px' }}>{plan.description}</p>}
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
            {plan.episodes.map((ep) => <li key={ep.title} style={{ marginBottom: 4 }}><b>{ep.title}</b><br /><span className="sub-dim">{ep.angle}</span></li>)}
          </ol>
          <div className="row" style={{ gap: 6, marginTop: 8 }}>
            {made ? <span className="chip">on the board, in order</span> : <>
              <button type="button" className="btn tiny pri" disabled={busy !== null} onClick={() => void make()}>{busy === 'make' ? 'Making…' : 'Make this series'}</button>
              <button type="button" className="btn tiny" onClick={() => setPlan(null)}>Not this</button>
            </>}
          </div>
        </div>
      )}
    </div>
  );
}
